import React, { useEffect, useState } from "react";
import { CompareButton } from "@/components/DamageInvoice";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";

function Photo({ taskId, id, room, flagged, onOpen }) {
  return (
    <div className={`relative rounded-lg ${flagged ? "ring-2 ring-rose-500" : ""}`}>
      <PhotoImg taskId={taskId} id={id} onOpen={onOpen} />
      {room && <span className={`absolute bottom-1 left-1 text-[10px] ${flagged ? "bg-rose-600 text-white" : "bg-white/90"} rounded px-1.5 py-0.5 pointer-events-none`} data-testid={`gallery-photo-room-${id}`}>{room}</span>}
    </div>
  );
}

function PhotoImg({ taskId, id, onOpen }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/cleaning-tasks/${taskId}/photos/${id}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [taskId, id]);
  return src
    ? <button onClick={() => onOpen(src)} className="aspect-square overflow-hidden rounded-lg" data-testid={`gallery-photo-${id}`}><img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" /></button>
    : <div className="aspect-square rounded-lg bg-slate-100 animate-pulse" />;
}

function PropertyGallery({ p, onOpen }) {
  const [i, setI] = useState(0);
  const t = p.turnovers[i];
  return (
    <section className="ns-card p-5" data-testid={`gallery-property-${p.property_id}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="font-serif text-xl">{(p.property_name || "").replace(/\*/g, " · ")}</div>
          <div className="text-xs text-slate-500 flex items-center gap-2">
            Turnover {t.date} · {t.photos.length} photos · {t.cleaner}
            {t.flagged_rooms?.length > 0 && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-rose-600 text-white rounded px-1.5 py-0.5" data-testid={`gallery-flagged-${p.property_id}`} title={t.flagged_rooms.join(", ")}>{t.flagged_rooms.length} room(s) flagged</span>}
            {t.rating ? <span className="inline-flex items-center gap-0.5 text-amber-600"><Star className="w-3 h-3 fill-current" />{t.rating}</span> : null}
          </div>
        </div>
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <CompareButton taskId={t.task_id} title={(p.property_name || "").replace(/\*/g, " · ")} testId={`gallery-compare-${p.property_id}`} />
          <button disabled={i >= p.turnovers.length - 1} onClick={() => setI(i + 1)} data-testid={`gallery-older-${p.property_id}`}
            className="p-1.5 rounded-full border border-slate-200 disabled:opacity-30 hover:bg-slate-50"><ChevronLeft className="w-4 h-4" /></button>
          <span className="px-1">{i + 1}/{p.turnovers.length}</span>
          <button disabled={i === 0} onClick={() => setI(i - 1)} data-testid={`gallery-newer-${p.property_id}`}
            className="p-1.5 rounded-full border border-slate-200 disabled:opacity-30 hover:bg-slate-50"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
        {t.photos.map((ph) => <Photo key={ph.id} taskId={t.task_id} id={ph.id} room={ph.room} flagged={t.flagged_rooms?.includes(ph.room)} onOpen={onOpen} />)}
      </div>
    </section>
  );
}

export default function TurnoverPhotos() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/turnover-gallery").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  return (
    <Layout title="Turnover photos" subtitle="Spot-check cleaning quality — latest turnover per property, flick back through earlier ones. Photos are kept for 90 days.">
      <div className="space-y-5" data-testid="turnover-gallery">
        {rows && rows.length === 0 && <div className="text-sm text-slate-400 italic" data-testid="gallery-empty">No check-out photos yet.</div>}
        {rows?.map((p) => <PropertyGallery key={p.property_id} p={p} onOpen={setOpen} />)}
      </div>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6 cursor-zoom-out" onClick={() => setOpen(null)} data-testid="gallery-lightbox">
          <img src={open} alt="" className="max-h-full max-w-full rounded-lg" />
        </div>
      )}
    </Layout>
  );
}
